import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { Address } from "../../models/common";
import type { Cargo } from "../../models/Cargo";

/**
 * 格式化货源价格。
 */
export const formatCargoPrice = (cargo: Cargo | null | undefined): string => {
  if (!cargo) {
    return "-";
  }
  if (cargo.single_price != null) {
    return `${cargo.single_price} 元/车`;
  }
  if (cargo.unit_price != null) {
    return `${cargo.unit_price} 元/吨`;
  }
  return "-";
};

/**
 * 格式化货源地址。
 */
export const formatCargoAddress = (address: Address | null | undefined): string => {
  if (!address) {
    return "-";
  }
  const parts = [address.province, address.city, address.district, address.address].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
};

/**
 * 格式化装车地 → 卸车地。
 */
export const formatCargoRoute = (cargo: Cargo | null | undefined): string => {
  if (!cargo?.from || !cargo?.to) {
    return "-";
  }
  const from = `${cargo.from.city || ""}${cargo.from.district ? `·${cargo.from.district}` : ""}`;
  const to = `${cargo.to.city || ""}${cargo.to.district ? `·${cargo.to.district}` : ""}`;
  if (!from && !to) {
    return "-";
  }
  return `${from || "-"} → ${to || "-"}`;
};

/**
 * 从 getOne / getMany 响应中解析 Cargo。
 */
export const parseCargoRecord = (raw: unknown): Cargo | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (record.data && typeof record.data === "object" && !Array.isArray(record.data)) {
    return parseCargoRecord(record.data);
  }
  const id = record.id ?? record._id;
  if (id === undefined || id === null || id === "") {
    return null;
  }
  return {
    ...(record as unknown as Cargo),
    id: String(id),
  };
};

/**
 * 货源信息展示（列表紧凑 / 详情完整）。
 */
export const AgentCargoInfoView: React.FC<{
  cargo: Cargo | null;
  loading?: boolean;
  compact?: boolean;
  emptyText?: string;
}> = ({ cargo, loading = false, compact = false, emptyText = "暂无货源信息" }) => {
  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        正在加载货源...
      </Typography>
    );
  }

  if (!cargo) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  if (compact) {
    return (
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 220, maxWidth: 320 }}>
        {cargo.image ? (
          <Box
            component="img"
            src={cargo.image}
            alt={cargo.name || "货源图片"}
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1,
              objectFit: "cover",
              flexShrink: 0,
              bgcolor: "action.hover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1,
              flexShrink: 0,
              bgcolor: "action.hover",
            }}
          />
        )}
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap title={cargo.name}>
            {cargo.name || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap title={formatCargoPrice(cargo)}>
            {formatCargoPrice(cargo)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap title={formatCargoRoute(cargo)}>
            {formatCargoRoute(cargo)}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {cargo.image ? (
          <Box
            component="img"
            src={cargo.image}
            alt={cargo.name || "货源图片"}
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              objectFit: "cover",
              flexShrink: 0,
              bgcolor: "action.hover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              flexShrink: 0,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              无图片
            </Typography>
          </Box>
        )}
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ wordBreak: "break-word" }}>
            {cargo.name || "-"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            货源 ID：{cargo.id || "-"}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatCargoPrice(cargo)}
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            装车地
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {formatCargoAddress(cargo.from)}
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            卸车地
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {formatCargoAddress(cargo.to)}
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
};
